import { LitElement, html, css, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import tailwindStyles from "../../index.css?inline";

interface StepDetails {
  stepper?: boolean;
  [key: string]: any;
}

interface Feed {
  _details?: StepDetails;
  [key: string]: any;
}

@customElement('custom-stepper')
export class CustomStepper extends LitElement {
  static styles = [
    unsafeCSS(tailwindStyles),
    css`
      :host {
        display: block;
        width: 100%;
        box-sizing: border-box;
      }

      .step-label {
        background-color: var(--md-sys-color-surface);
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
      }

      .step-label-text {
        color: var(--md-sys-color-onSurface);
      }

      .progress-bar {
        transition: background-color 0.3s ease;
      }

      .progress-bar.active {
        background-color: var(--md-sys-color-primary);
      }

      .progress-bar.inactive {
        background-color: var(--md-sys-color-secondaryContainer);
      }

      .progress-dot {
        border: 2px solid var(--md-sys-color-secondaryContainer);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }

      /* Hide native scrollbars for a clean look while keeping scroll functionality active */
      .scroll-container::-webkit-scrollbar {
        display: none;
      }
      .scroll-container {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
    `
  ];

  @property({ type: Array }) steps: any[] = [];
  @property({ type: Number }) activeStep = 0;
  @property({ type: String }) currentStepLabel = 'Step 1';
  @property({ type: Array }) feeds: Feed[] = [];

  // Check if stepper should be hidden
  private get shouldHideStepper(): boolean {
    if (!this.feeds || !this.feeds[this.activeStep]) return false;
    const currentFeed = this.feeds[this.activeStep];
    return currentFeed._details?.stepper === false;
  }

  private _onStepClick(index: number): void {
    this.dispatchEvent(new CustomEvent('step-click', {
      detail: { index },
      bubbles: true,
      composed: true
    }));
  }

  render() {
    // If stepper should be hidden, return empty template
    if (this.shouldHideStepper) {
      return html``;
    }

    return html`
      <div class="flex flex-col gap-2 items-start bg-transparent w-full box-border overflow-hidden">
        
        <!-- Step labels row (Optimized for mobile touch horizontal scrolling and fluidity) -->
        <div class="flex items-center gap-2 w-full overflow-x-auto pb-1 scroll-container snap-x">
          ${this.steps?.map((_, index) => html`
            <div
              class="flex justify-start cursor-pointer flex-1 min-w-[70px] sm:min-w-0 snap-chip"
              @click="${() => this._onStepClick(index)}"
            >
              <div class="text-center truncate px-2 sm:px-4 py-1.5 rounded-md border border-[var(--md-sys-color-outlineVariant)] bg-[var(--md-sys-color-surface)] text-[var(--md-sys-color-onSurface)] font-semibold text-[11px] sm:text-xs mb-3 shadow-sm select-none break-all whitespace-nowrap overflow-hidden text-ellipsis">
                ${index === this.activeStep ? this.currentStepLabel : `Step ${index + 1}`}
              </div>
            </div>
          `)}
        </div>

        <!-- Progress Bars -->
        <div class="flex items-center gap-2 w-full box-border">
          ${this.steps?.map((_, index) => html`
            <div
              class="progress-bar h-2 rounded-full relative transition-colors duration-300 min-w-0 flex-1 cursor-pointer ${index + 1 <= this.activeStep ? 'active' : 'inactive'}"
              @click="${() => this._onStepClick(index)}"
            >
              ${index === this.activeStep - 1 ? html`
                <div class="progress-dot absolute right-0 top-1/2 translate-x-[10%] -translate-y-1/2 w-4 h-4 bg-(--md-sys-color-primary) rounded-full"></div>
              ` : ''}
            </div>
          `)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'custom-stepper': CustomStepper;
  }
}